import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ResourceLease } from "../geometry/FinishedGeometryCache";

/**
 * Acquire only after commit: abandoned/suspended renders must not leak leases
 * or native resources.
 *
 * ## The lag this name is about
 *
 * For one render after `key` changes, this returns the resource of the
 * *previous* key. A caller that derives anything else from the same inputs —
 * a rotation, a material, a sibling geometry, a size handed to a shader — pairs
 * the current value of that with the previous resource for that render. For
 * example a pad gaining its first cutout flips `geometryKind` and `hasCutouts`
 * together, and `PadRenderer` builds `meshProps.rotation` from the new
 * `hasCutouts` while this still hands back the pre-cutout block.
 *
 * It is invisible today because the `setResource` below is scheduled from a
 * layout effect, so React commits the corrected render before the browser
 * paints. That is a scheduling property of React, not a guarantee this module
 * can enforce, which is why the name says `Latest` rather than the resource
 * being in step with the key. A caller that cannot tolerate one mismatched
 * render must gate on the key itself rather than on this value being non-null.
 *
 * ## Why it does not simply return null
 *
 * While a new key is in flight this keeps returning the *previous* value
 * rather than null. Returning null made every renderer that owns geometry bail
 * out for one render (`if (!panelGeometry) return null`), which unmounted the
 * whole `<group><mesh>` subtree and mounted a fresh one a commit later. Both
 * commits land before the same paint, so it was invisible — but it destroyed
 * and rebuilt every React-owned material under that mesh on every build, and a
 * resize drag rebuilds a part on every frame.
 *
 * What that cost: React runs every layout-effect cleanup in a commit before
 * any setup, so all holders of a shared program were disposed before a
 * replacement existed. three deletes a program once its use count reaches
 * zero, so the next frame had to link it again — measured as 5 `linkProgram`
 * and 7 `deleteProgram` calls during a single 20-move drag, each one a
 * synchronous driver stall.
 *
 * The lease is therefore acquired *before* the old one is released, and the
 * release happens in the effect body rather than in a cleanup. That ordering
 * is what makes returning the stale value safe: the commit that still renders
 * the old resource runs before this effect, so the resource it points at is
 * still leased while it is attached, and cannot be evicted underneath it.
 * Unmount is the one case that must still release, which the second effect
 * does.
 *
 * ## Attaching a leased value
 *
 * Equal keys hand the same cached object to every holder, so a value from here
 * goes onto its mesh as a prop (`<mesh geometry={value}>`), never through
 * `<primitive object={value} attach="...">`. With @react-three/fiber 8 a
 * primitive keeps its reconciler descriptor (`__r3f`) on the object itself, and
 * `prepare` overwrites it for every sharer: only the last-attached sharer owns
 * it, the other sharers' updates are skipped (they stay on the old value), the
 * last one can receive another key's value, and unmounting one sharer disposes
 * an object the others still draw. R3F never disposes a prop value, which is
 * what the lease wants anyway.
 */
export function useLatestCommittedResource<T>(
  key: string | null,
  acquire: () => ResourceLease<T>,
): T | null {
  const [resource, setResource] = useState<{ key: string; value: T } | null>(
    null,
  );
  const leaseRef = useRef<ResourceLease<T> | null>(null);
  /**
   * Leases whose value is still attached to a mesh. `setResource` only
   * *schedules* the commit that swaps the mesh over, so the previous value is
   * on screen until that commit lands — and a release can dispose immediately
   * (angle-section leases always dispose their geometry and materials, profile
   * leases their materials, and the cache can evict an oversized entry on
   * release). Released by the passive effect below, which runs after the commit
   * that attached the replacement.
   */
  const retiredRef = useRef<Array<ResourceLease<T>>>([]);

  useLayoutEffect(() => {
    if (key === null) {
      const current = leaseRef.current;
      leaseRef.current = null;
      if (current) retiredRef.current.push(current);
      setResource(null);
      return;
    }
    const previous = leaseRef.current;
    const lease = acquire();
    leaseRef.current = lease;
    setResource({ key, value: lease.value });
    if (previous) retiredRef.current.push(previous);
    // The key describes every resource input; changing factory identity is not
    // a resource change (store rebuilds clone otherwise identical definitions).
    // biome-ignore lint/correctness/useExhaustiveDependencies: value-based ownership
  }, [key]);

  // Runs after the commit that attached whatever `resource` now holds, which is
  // the first moment the retired values are off every mesh.
  //
  // A lease still represented by the committed resource is kept back rather
  // than released. React may flush this effect before it processes a state
  // update made from a layout effect, so when a parent advances the key while
  // the first `setResource` is still pending, the committed value can be the
  // very one this queue holds — and an angle-section release disposes its
  // geometry and materials immediately.
  useEffect(() => {
    const retired = retiredRef.current;
    if (retired.length === 0) return;
    const committed = resource?.value;
    const stillAttached = retired.filter((lease) => lease.value === committed);
    retiredRef.current = stillAttached;
    for (const lease of retired) {
      if (lease.value !== committed) lease.release();
    }
  }, [resource]);

  // Release on unmount only. A key change hands the lease over above, so a
  // cleanup here would put the release back before the acquire.
  useEffect(
    () => () => {
      leaseRef.current?.release();
      leaseRef.current = null;
      const retired = retiredRef.current;
      retiredRef.current = [];
      for (const lease of retired) lease.release();
    },
    [],
  );

  return key !== null ? (resource?.value ?? null) : null;
}
